import React, { useCallback, useMemo } from 'react';
import { Menu, MenuItemProps } from '@televet/kibble-ui/build/components/Menu';
import { useGetAppointmentOptionsLazyQuery } from 'shared/types/graphql';
import sortBy from 'lodash-es/sortBy';
import useClinicUser from 'shared/hooks/useClinicUser';

interface FilterAppointmentsMenuProps {
  selectedRooms: string[];
  selectedTypes: string[];
  selectedProviders: string[];
  onChange?: (selectedRooms: string[], selectedTypes: string[], selectedProviders: string[]) => void;
}

const FilterAppointmentsMenu = ({
  selectedRooms,
  selectedTypes,
  selectedProviders,
  onChange,
}: FilterAppointmentsMenuProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const [getAppointmentOptions, { data: appointmentOptions }] = useGetAppointmentOptionsLazyQuery({
    variables: {
      clinicId: currentClinicId || '',
    },
  });

  const roomOptions: MenuItemProps[] = useMemo(
    () =>
      appointmentOptions?.clinicRooms?.map(
        (room): MenuItemProps => ({
          label: room.name,
          value: room.id,
          groupLabel: 'Appointment Rooms',
          isSelected: selectedRooms.includes(room.id),
        }),
      ) || [],
    [appointmentOptions?.clinicRooms, selectedRooms],
  );

  const providerOptions: MenuItemProps[] = useMemo(
    () =>
      appointmentOptions?.clinicEmployees?.map(
        (provider): MenuItemProps => ({
          label: `${provider.firstName} ${provider.lastName}`,
          value: provider.id,
          groupLabel: 'Appointment Providers',
          isSelected: selectedProviders.includes(provider.id),
        }),
      ) || [],
    [appointmentOptions?.clinicEmployees, selectedProviders],
  );

  const typeOptions: MenuItemProps[] = useMemo(
    () =>
      appointmentOptions?.appointmentTypes?.map(
        (appointmentType): MenuItemProps => ({
          label: appointmentType.name,
          value: appointmentType.id,
          groupLabel: 'Appointment Types',
          isSelected: selectedTypes.includes(appointmentType.id),
        }),
      ) || [],
    [appointmentOptions?.appointmentTypes, selectedTypes],
  );

  const allOptions = useMemo(
    () => sortBy([...typeOptions, ...providerOptions, ...roomOptions], 'groupLabel', 'label'),
    [providerOptions, roomOptions, typeOptions],
  );

  const onCloseMenu = useCallback(
    (items: (MenuItemProps | undefined)[]) => {
      if (items) {
        const selectedRooms = items
          .filter((item) => item?.groupLabel === 'Appointment Rooms')
          .map((item) => item?.value || '');
        const selectedTypes = items
          .filter((item) => item?.groupLabel === 'Appointment Types')
          .map((item) => item?.value || '');
        const selectedProviders = items
          .filter((item) => item?.groupLabel === 'Appointment Providers')
          .map((item) => item?.value || '');
        onChange?.(selectedRooms, selectedTypes, selectedProviders);
      }
    },
    [onChange],
  );

  const isFiltering = useMemo(
    () => !!selectedProviders.length || !!selectedRooms.length || !!selectedTypes.length,
    [selectedProviders.length, selectedRooms.length, selectedTypes.length],
  );

  return (
    <Menu
      containerProps={{
        onOpen: getAppointmentOptions,
        onClose: onCloseMenu,
      }}
      buttonProps={{
        variant: isFiltering ? 'ghost' : 'ghostNeutral',
        ...(isFiltering && { bg: 'background.secondaryAlpha.hover' }),
        size: 'sm',
        leftIconName: 'filter',
        showRightIcon: false,
      }}
      listProps={{
        isGrouped: true,
        isMultiSelect: true,
        isSearchable: true,
        menuItems: allOptions,
      }}
    />
  );
};

export default FilterAppointmentsMenu;
